import { SIGNATURE_FONTS, type AdoptSignatureRequest, type AdoptedSignature, type SignatureFontId } from "@gsignatures/core";
import { useId, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { S } from "../strings";
import { SignaturePad } from "./SignaturePad";

type Tab = "type" | "draw" | "upload";
const TABS: Tab[] = ["type", "draw", "upload"];
const MAX_UPLOAD = 5 * 1024 * 1024;

interface Props {
  name: string;
  current: AdoptedSignature | undefined;
  onClose: () => void;
  /** Saves it on the server; resolves true when adopted. */
  onAdopt: (req: AdoptSignatureRequest) => Promise<boolean>;
}

/** A photo of a signature, shrunk to a sensible size and turned into a PNG. */
async function normaliseImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = Math.min(1, 1200 / img.naturalWidth, 600 / img.naturalHeight);
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.naturalWidth * scale));
    c.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = c.getContext("2d");
    if (!ctx) throw new Error("no canvas");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function AdoptModal({ name, current, onClose, onAdopt }: Props) {
  const [tab, setTab] = useState<Tab>(current?.kind === "image" ? "draw" : "type");
  const [text, setText] = useState(current?.kind === "type" ? current.text : name);
  const [font, setFont] = useState<SignatureFontId>(current?.kind === "type" ? current.font : SIGNATURE_FONTS[0].id);
  const [drawn, setDrawn] = useState<string | null>(null);
  const [upload, setUpload] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const styleRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const base = useId();

  const ready = tab === "type" ? text.trim().length > 1 : tab === "draw" ? !!drawn : !!upload;

  async function adopt() {
    if (!ready || busy) return;
    const req: AdoptSignatureRequest =
      tab === "type" ? { kind: "type", text: text.trim(), font } : { kind: "image", dataUrl: (tab === "draw" ? drawn : upload)! };
    setBusy(true);
    const ok = await onAdopt(req);
    setBusy(false);
    if (ok) onClose();
  }

  function onTabKey(e: React.KeyboardEvent, i: number) {
    const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const j = (i + d + TABS.length) % TABS.length;
    setTab(TABS[j]!);
    tabRefs.current[j]?.focus();
  }

  function onStyleKey(e: React.KeyboardEvent, i: number) {
    const d = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const j = (i + d + SIGNATURE_FONTS.length) % SIGNATURE_FONTS.length;
    setFont(SIGNATURE_FONTS[j]!.id);
    styleRefs.current[j]?.focus();
  }

  async function onFile(f: File | undefined) {
    setUploadErr(null);
    if (!f) return;
    if (!/^image\/(png|jpeg)$/.test(f.type)) {
      setUploadErr(S.adopt.uploadType);
      return;
    }
    if (f.size > MAX_UPLOAD) {
      setUploadErr(S.adopt.uploadTooBig);
      return;
    }
    try {
      setUpload(await normaliseImage(f));
    } catch {
      setUploadErr(S.adopt.uploadType);
    }
  }

  return (
    <Modal title={S.adopt.title} onClose={onClose} wide>
      <div className="tabs" role="tablist" aria-label={S.adopt.tabsLabel}>
        {TABS.map((t, i) => (
          <button
            key={t}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${base}-tab-${t}`}
            aria-selected={tab === t}
            aria-controls={`${base}-panel`}
            tabIndex={tab === t ? 0 : -1}
            className={tab === t ? "on" : ""}
            onClick={() => setTab(t)}
            onKeyDown={(e) => onTabKey(e, i)}
          >
            {S.adopt.tabs[t]}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${base}-panel`} aria-labelledby={`${base}-tab-${tab}`}>
        {tab === "type" && (
          <div className="stack gap-12">
            <div className="field">
              <label htmlFor={`${base}-name`}>{S.adopt.name}</label>
              <input id={`${base}-name`} className="input" value={text} autoComplete="name" onChange={(e) => setText(e.target.value)} data-autofocus />
            </div>
            <div className="styles" role="radiogroup" aria-label={S.adopt.style}>
              {SIGNATURE_FONTS.map((f, i) => (
                <button
                  key={f.id}
                  ref={(el) => {
                    styleRefs.current[i] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={font === f.id}
                  aria-label={`${f.family}`}
                  tabIndex={font === f.id ? 0 : -1}
                  className={`style-opt ${font === f.id ? "on" : ""}`}
                  style={{ fontFamily: `"${f.family}", cursive`, fontSize: f.size }}
                  onClick={() => setFont(f.id)}
                  onKeyDown={(e) => onStyleKey(e, i)}
                >
                  <span className="style-text">{text.trim() || name}</span>
                  {font === f.id && (
                    <span className="style-check" aria-hidden="true">
                      <Icon n="check" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {tab === "draw" && <SignaturePad onChange={setDrawn} />}
        {tab === "upload" && (
          <div className="stack gap-8">
            <label className="drop" htmlFor={`${base}-file`}>
              {upload ? (
                <img src={upload} alt={S.adopt.uploadAlt} />
              ) : (
                <span className="drop-in">
                  <Icon n="upload" size={22} />
                  <span>{S.adopt.upload}</span>
                  <span className="small faint">{S.adopt.uploadHelp}</span>
                </span>
              )}
              <input type="file" id={`${base}-file`} accept="image/png,image/jpeg" className="sr" onChange={(e) => void onFile(e.target.files?.[0])} />
            </label>
            <div aria-live="polite">
              {uploadErr ? (
                <div className="err">
                  <Icon n="alert" />
                  <span>{uploadErr}</span>
                </div>
              ) : upload ? (
                <label htmlFor={`${base}-file`} className="btn-link">
                  {S.adopt.replace}
                </label>
              ) : null}
            </div>
          </div>
        )}
      </div>
      <p className="help">
        {S.adopt.agree.before}
        <b>{S.adopt.agree.button}</b>
        {S.adopt.agree.after}
      </p>
      <div className="row end">
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          {S.common.cancel}
        </button>
        <button type="button" className="btn btn-primary" disabled={!ready || busy} onClick={() => void adopt()} aria-busy={busy}>
          {busy ? S.adopt.adopting : S.adopt.adopt}
        </button>
      </div>
    </Modal>
  );
}
